/**
 * Admin Users Page — list users, create new, edit via modal, manage roles
 */

import { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

type Breakpoint = 'mobile' | 'tablet' | 'desktop';

export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(getBreakpoint());
  useEffect(() => {
    const handler = () => setBp(getBreakpoint());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return bp;
}

function getBreakpoint(): Breakpoint {
  const w = window.innerWidth;
  if (w < 640) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function LockIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function PencilIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  );
}

function CameraIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

// Grid column templates
const COL_DESKTOP = '32px minmax(100px, 1fr) minmax(140px, 2fr) minmax(100px, 1fr) 60px 85px 160px';
const COL_TABLET  = '32px minmax(80px, 1fr) minmax(100px, 2fr) 60px 110px';

// ─── Glass constants ────────────────────────────────────────────────────────
const GLASS_BG = 'rgba(255, 255, 255, 0.03)';
const GLASS_BORDER = '1px solid rgba(255, 255, 255, 0.08)';
const GLASS_BLUR = 'blur(24px) saturate(180%)';
const GLASS_SHADOW = '0 4px 24px rgba(0, 0, 0, 0.25), 0 1px 2px rgba(0, 0, 0, 0.15)';
const ROW_DIVIDER = '1px solid rgba(255, 255, 255, 0.04)';

// ─────────────────────────────────────────────────────────────────────────────
// EditUserModal
// ─────────────────────────────────────────────────────────────────────────────

interface EditModalProps {
  user: any;
  clients: any[];
  canChangeRole: boolean;
  canDelete: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

export function EditUserModal({ user, clients, canChangeRole, canDelete, onClose, onSaved, onDeleted }: EditModalProps) {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';

  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [role, setRole] = useState(user.role || 'client');
  const [status, setStatus] = useState(user.status || 'active');
  const [clientId, setClientId] = useState(user.clients?.[0]?.clientId || '');
  const [clientRole, setClientRole] = useState(user.clients?.[0]?.role || 'member');
  const [photoPreview, setPhotoPreview] = useState<string | null>(user.profilePhotoPath || null);
  const [photoData, setPhotoData] = useState<string | null>(null);
  const [removePhoto, setRemovePhoto] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fileRef = useRef<HTMLInputElement>(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setPhotoData(result);
      setPhotoPreview(result);
      setRemovePhoto(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setPhotoData(null);
    setPhotoPreview(null);
    setRemovePhoto(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // 1. Save profile fields
      const updates: any = {};
      if (name !== (user.name || '')) updates.name = name || null;
      if (email !== user.email) updates.email = email;
      if (role !== user.role) updates.role = role;
      if (status !== (user.status || 'active')) updates.status = status;
      if (newPassword.trim()) updates.password = newPassword;

      // Client organization assignment
      const origClientId = user.clients?.[0]?.clientId || '';
      const origClientRole = user.clients?.[0]?.role || 'member';
      if (clientId !== origClientId || clientRole !== origClientRole) {
        updates.clientId = clientId;
        updates.clientRole = clientRole;
      }

      if (Object.keys(updates).length > 0) {
        await api.admin.updateUser(user.id, updates);
      }

      // 2. Handle photo
      if (photoData) {
        await api.admin.uploadUserPhoto(user.id, photoData);
      } else if (removePhoto && user.profilePhotoPath) {
        await api.admin.deleteUserPhoto(user.id);
      }

      setSuccess('Saved');
      setTimeout(() => { onSaved(); onClose(); }, 600);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this user? This cannot be undone.')) return;
    try {
      await api.admin.deleteUser(user.id);
      onDeleted();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  };

  const initials = (name || email || '?').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    // Backdrop
    <div style={modal.backdrop} onClick={onClose}>
      <div
        style={{ ...modal.container, ...(isMobile ? { width: '95vw', maxHeight: '92vh', margin: '4vh auto' } : {}) }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={modal.header}>
          <h2 style={modal.title}>Edit User</h2>
          <button onClick={onClose} style={modal.closeBtn}>&times;</button>
        </div>

        {/* Scrollable body */}
        <div style={modal.body}>

          {/* Profile photo */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
            <div
              style={{
                width: 80, height: 80, borderRadius: '50%',
                background: photoPreview ? `url(${photoPreview}) center/cover no-repeat` : 'rgba(79, 124, 255, 0.15)',
                border: '2px solid rgba(255,255,255,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#4F7CFF', fontSize: 24, fontWeight: 700,
                position: 'relative', cursor: 'pointer', overflow: 'hidden',
              }}
              onClick={() => fileRef.current?.click()}
            >
              {!photoPreview && initials}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(0,0,0,0.5)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                opacity: 0, transition: 'opacity 0.2s',
              }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0'; }}
              >
                <CameraIcon size={22} />
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoSelect}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" onClick={() => fileRef.current?.click()} style={modal.linkBtn}>
                Upload Photo
              </button>
              {photoPreview && (
                <button type="button" onClick={handleRemovePhoto} style={{ ...modal.linkBtn, color: '#ef4444' }}>
                  Remove
                </button>
              )}
            </div>
          </div>

          {/* Form fields */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
            <label style={modal.fieldLabel}>
              <span style={modal.labelText}>Name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={modal.input}
                placeholder="Full name"
              />
            </label>
            <label style={modal.fieldLabel}>
              <span style={modal.labelText}>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={modal.input}
                placeholder="Email address"
              />
            </label>
            <label style={modal.fieldLabel}>
              <span style={modal.labelText}>Role</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                style={modal.input}
                disabled={!canChangeRole}
              >
                <option value="client">Client</option>
                <option value="sales">Sales</option>
                <option value="operations">Operations</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <label style={modal.fieldLabel}>
              <span style={modal.labelText}>Status</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={modal.input}
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>
            </label>
          </div>

          {/* Organization assignment — only for client role users */}
          {role === 'client' && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : clientId ? '1fr 1fr' : '1fr', gap: 14, marginTop: 14 }}>
              <label style={modal.fieldLabel}>
                <span style={modal.labelText}>Organization</span>
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  style={modal.input}
                >
                  <option value="">No organization</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              {clientId && (
                <label style={modal.fieldLabel}>
                  <span style={modal.labelText}>Org Role</span>
                  <select
                    value={clientRole}
                    onChange={(e) => setClientRole(e.target.value)}
                    style={modal.input}
                  >
                    <option value="viewer">General Viewer</option>
                    <option value="commenter">User / Commenter</option>
                    <option value="people_manager">People Manager</option>
                    <option value="client_admin">Client Admin</option>
                  </select>
                </label>
              )}
            </div>
          )}

          {/* Password reset section */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <span style={{ ...modal.labelText, marginBottom: 8, display: 'block' }}>
              <LockIcon size={12} /> Reset Password
            </span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', ...(isMobile ? { flexDirection: 'column' as const } : {}) }}>
              <div style={{ position: 'relative', flex: 1, ...(isMobile ? { width: '100%' } : {}) }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ ...modal.input, paddingRight: 60 }}
                  placeholder="Leave blank to keep current"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', color: '#64748b', cursor: 'pointer',
                    fontSize: 11, padding: '2px 6px',
                  }}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            {newPassword && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                User will be required to change this password on next login.
              </div>
            )}
          </div>

          {/* User meta */}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 20, fontSize: 12, color: '#64748b' }}>
            <span>Created: {new Date(user.createdAt).toLocaleDateString()}</span>
            <span>Updated: {new Date(user.updatedAt).toLocaleDateString()}</span>
            {user.clerkId && <span>Clerk linked</span>}
          </div>

          {/* Feedback */}
          {error && <div style={{ ...modal.feedback, background: 'rgba(127, 29, 29, 0.6)', color: '#fca5a5' }}>{error}</div>}
          {success && <div style={{ ...modal.feedback, background: 'rgba(5, 46, 22, 0.7)', color: '#86efac' }}>{success}</div>}
        </div>

        {/* Footer */}
        <div style={modal.footer}>
          {canDelete && (
            <button onClick={handleDelete} style={modal.deleteBtn}>Delete User</button>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={modal.cancelBtn}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={modal.saveBtn}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal styles ───────────────────────────────────────────────────────────

export const modal: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  container: {
    width: 520, maxHeight: '85vh',
    background: 'rgba(20, 20, 24, 0.92)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 20,
    boxShadow: '0 24px 80px rgba(0,0,0,0.5), 0 0 1px rgba(255,255,255,0.1)',
    display: 'flex', flexDirection: 'column' as const,
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 24px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  title: {
    fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0,
  },
  closeBtn: {
    background: 'none', border: 'none', color: '#64748b',
    fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: '0 4px',
  },
  body: {
    padding: '20px 24px', overflowY: 'auto' as const, flex: 1,
  },
  fieldLabel: {
    display: 'flex', flexDirection: 'column' as const, gap: 4,
  },
  labelText: {
    fontSize: 12, fontWeight: 600, color: '#94a3b8',
    textTransform: 'uppercase' as const, letterSpacing: '0.04em',
    display: 'flex', alignItems: 'center', gap: 6,
  },
  input: {
    background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: 10, padding: '10px 14px', color: '#f1f5f9', fontSize: 14,
    outline: 'none', boxSizing: 'border-box' as const, width: '100%',
  },
  linkBtn: {
    background: 'none', border: 'none', color: '#4F7CFF',
    fontSize: 12, cursor: 'pointer', padding: 0, fontWeight: 500,
  },
  feedback: {
    marginTop: 14, padding: '8px 14px', borderRadius: 10, fontSize: 13,
    border: '1px solid rgba(255,255,255,0.05)',
  },
  footer: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '14px 24px 18px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  deleteBtn: {
    background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)',
    color: '#ef4444', borderRadius: 10, padding: '8px 16px',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  cancelBtn: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
    color: '#94a3b8', borderRadius: 10, padding: '8px 18px',
    fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
  saveBtn: {
    background: 'rgba(79, 124, 255, 0.85)', border: '1px solid rgba(79, 124, 255, 0.3)',
    color: '#fff', borderRadius: 10, padding: '8px 22px',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(79, 124, 255, 0.2)',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// BulkDeleteUsersModal
// ─────────────────────────────────────────────────────────────────────────────

function BulkDeleteUsersModal({ ids, onClose, onDeleted }: { ids: string[]; onClose: () => void; onDeleted: () => void }) {
  const [info, setInfo] = useState<Array<{ id: string; name: string | null; email: string; role: string; hasClerk: boolean; organizations: string[] }>>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.admin.bulkUserInfo(ids)
      .then(setInfo)
      .catch((err: any) => setError(err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const clerkCount = info.filter((u) => u.hasClerk).length;
  const orgCount = info.reduce((sum, u) => sum + u.organizations.length, 0);
  const confirmRequired = `DELETE ${info.length}`;
  const canDelete = confirmText === confirmRequired;

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await api.admin.bulkDeleteUsers(ids);
      onDeleted();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
      setDeleting(false);
    }
  };

  return (
    <div style={modal.backdrop} onClick={onClose}>
      <div
        style={{ ...modal.container, width: 540 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={modal.header}>
          <h2 style={{ ...modal.title, color: '#ef4444' }}>Delete {ids.length} User{ids.length > 1 ? 's' : ''}</h2>
          <button onClick={onClose} style={modal.closeBtn}>&times;</button>
        </div>

        <div style={modal.body}>
          {loading ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>Loading impact summary...</div>
          ) : (
            <>
              {error && <div style={{ background: 'rgba(127, 29, 29, 0.6)', color: '#fca5a5', padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 12 }}>{error}</div>}

              {/* Impact summary */}
              <div style={{
                background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
                padding: 16, marginBottom: 8,
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#f59e0b', marginBottom: 12 }}>
                  This action will permanently:
                </div>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#94a3b8', fontSize: 13, lineHeight: 1.8 }}>
                  <li>Delete <strong style={{ color: '#f1f5f9' }}>{info.length} user account{info.length > 1 ? 's' : ''}</strong></li>
                  {clerkCount > 0 && <li>Remove <strong style={{ color: '#f1f5f9' }}>{clerkCount}</strong> from Clerk authentication</li>}
                  {orgCount > 0 && <li>Remove <strong style={{ color: '#f1f5f9' }}>{orgCount} organization membership{orgCount !== 1 ? 's' : ''}</strong></li>}
                </ul>
              </div>

              {/* User list */}
              <div style={{
                background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
                maxHeight: 200, overflowY: 'auto' as const,
              }}>
                {info.map((u) => (
                  <div key={u.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 13,
                  }}>
                    <div>
                      <span style={{ color: '#f1f5f9', fontWeight: 500 }}>{u.name || u.email}</span>
                      {u.name && <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>{u.email}</span>}
                    </div>
                    <span style={{ color: '#64748b', fontSize: 12 }}>
                      {u.role}{u.organizations.length > 0 ? ` · ${u.organizations.join(', ')}` : ''}
                    </span>
                  </div>
                ))}
              </div>

              {/* Confirmation input */}
              <div style={{ marginTop: 16 }}>
                <label style={{ ...modal.labelText, marginBottom: 8, display: 'block' }}>
                  Type <strong style={{ color: '#ef4444', fontFamily: 'monospace' }}>{confirmRequired}</strong> to confirm
                </label>
                <input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  style={modal.input}
                  placeholder={confirmRequired}
                  autoFocus
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button onClick={onClose} style={modal.cancelBtn}>Cancel</button>
                <button
                  onClick={handleDelete}
                  disabled={!canDelete || deleting}
                  style={{
                    background: canDelete ? 'rgba(239, 68, 68, 0.85)' : 'rgba(239, 68, 68, 0.2)',
                    color: canDelete ? '#fff' : '#666',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: 10, padding: '8px 22px', fontSize: 13, fontWeight: 600,
                    cursor: canDelete ? 'pointer' : 'not-allowed',
                  }}
                >
                  {deleting ? 'Deleting...' : `Delete ${info.length} User${info.length > 1 ? 's' : ''}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV Import Modal
// ─────────────────────────────────────────────────────────────────────────────

const CSV_HEADERS = ['email', 'password', 'name', 'role', 'organization', 'org_role'];
const CSV_TEMPLATE = [
  CSV_HEADERS.join(',') + ',,REFERENCE',
  'john@example.com,SecurePass123!,John Doe,client,123456,viewer,,organization: use the 6-digit Org Code (shown in client list) or exact org name',
  'jane@example.com,SecurePass456!,Jane Smith,client,123456,commenter,,role values: client | sales | operations | admin',
  'bob@example.com,SecurePass789!,Bob Manager,client,123456,people_manager,,org_role values: viewer | commenter | people_manager | client_admin',
].join('\n') + '\n';

export function CsvImportModal({ clients, onClose, onImported }: { clients: any[]; onClose: () => void; onImported: () => void }) {
  const [rows, setRows] = useState<Array<{ email: string; password: string; name: string; role: string; organization: string; orgRole: string }>>([]);
  const [parseError, setParseError] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<Array<{ email: string; success: boolean; error?: string }> | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text: string) => {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length < 2) {
      setParseError('CSV must have a header row and at least one data row');
      return;
    }

    // Parse header
    const header = lines[0].toLowerCase().split(',').map((h) => h.trim());
    const emailIdx = header.indexOf('email');
    const passwordIdx = header.indexOf('password');
    const nameIdx = header.indexOf('name');
    const roleIdx = header.indexOf('role');
    const orgIdx = header.findIndex((h) => h === 'organization' || h === 'org');
    const orgRoleIdx = header.findIndex((h) => h === 'org_role' || h === 'org role' || h === 'orgrole');

    if (emailIdx === -1 || passwordIdx === -1) {
      setParseError('CSV must have "email" and "password" columns');
      return;
    }

    const parsed = [];
    const errors = [];
    for (let i = 1; i < lines.length; i++) {
      // Simple CSV parsing (handles quoted fields with commas)
      // Split on commas, respecting quoted fields
      const cols: string[] = [];
      let current = '';
      let inQuotes = false;
      for (const ch of lines[i]) {
        if (ch === '"') { inQuotes = !inQuotes; continue; }
        if (ch === ',' && !inQuotes) { cols.push(current.trim()); current = ''; continue; }
        current += ch;
      }
      cols.push(current.trim());

      const email = cols[emailIdx] || '';
      const password = cols[passwordIdx] || '';

      if (!email) { errors.push(`Row ${i + 1}: missing email`); continue; }
      if (!password) { errors.push(`Row ${i + 1}: missing password for ${email}`); continue; }

      parsed.push({
        email,
        password,
        name: nameIdx >= 0 ? (cols[nameIdx] || '') : '',
        role: roleIdx >= 0 ? (cols[roleIdx] || 'client') : 'client',
        organization: orgIdx >= 0 ? (cols[orgIdx] || '') : '',
        orgRole: orgRoleIdx >= 0 ? (cols[orgRoleIdx] || 'viewer') : 'viewer',
      });
    }

    if (errors.length > 0) {
      setParseError(errors.join('; '));
    } else {
      setParseError('');
    }
    setRows(parsed);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => parseCSV(reader.result as string);
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const result = await api.admin.bulkCreateUsers(rows.map((r) => ({
        email: r.email,
        password: r.password,
        name: r.name || undefined,
        role: r.role || undefined,
        organization: r.organization || undefined,
        orgRole: r.orgRole || undefined,
      })));
      setResults(result.results);
      if (result.created > 0) {
        setTimeout(onImported, 2000);
      }
    } catch (err: any) {
      setParseError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Validate org identifiers against known clients (by code, name, or slug)
  const knownOrgIds = new Set([
    ...clients.map((c: any) => c.name.toLowerCase()),
    ...clients.map((c: any) => c.slug?.toLowerCase()).filter(Boolean),
    ...clients.map((c: any) => c.orgCode).filter(Boolean),
  ]);
  const unknownOrgs = [...new Set(rows.filter((r) => r.organization && !knownOrgIds.has(r.organization.toLowerCase()) && !knownOrgIds.has(r.organization)).map((r) => r.organization))];

  return (
    <div style={modal.backdrop} onClick={onClose}>
      <div
        style={{ ...modal.container, width: 640, maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={modal.header}>
          <h2 style={modal.title}>Import Users from CSV</h2>
          <button onClick={onClose} style={modal.closeBtn}>&times;</button>
        </div>

        <div style={{ ...modal.body, overflowY: 'auto' as const }}>
          {/* Results view */}
          {results ? (
            <>
              <div style={{ marginBottom: 16 }}>
                <span style={{ color: '#86efac', fontWeight: 600, fontSize: 14 }}>
                  {results.filter((r) => r.success).length} created
                </span>
                {results.some((r) => !r.success) && (
                  <span style={{ color: '#fca5a5', fontWeight: 600, fontSize: 14, marginLeft: 12 }}>
                    {results.filter((r) => !r.success).length} failed
                  </span>
                )}
              </div>
              <div style={{
                background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
                maxHeight: 300, overflowY: 'auto' as const,
              }}>
                {results.map((r, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 13,
                  }}>
                    <span style={{ color: '#f1f5f9' }}>{r.email}</span>
                    {r.success && !r.error ? (
                      <span style={{ color: '#86efac', fontSize: 12 }}>Created</span>
                    ) : r.success && r.error ? (
                      <span style={{ color: '#fcd34d', fontSize: 12 }}>{r.error}</span>
                    ) : (
                      <span style={{ color: '#fca5a5', fontSize: 12 }}>{r.error}</span>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button onClick={onClose} style={modal.saveBtn}>Done</button>
              </div>
            </>
          ) : (
            <>
              {/* Step 1: Download template / Upload file */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <button onClick={downloadTemplate} style={{
                  ...modal.cancelBtn, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  Download Template
                </button>
                <button onClick={() => fileRef.current?.click()} style={modal.saveBtn}>
                  Upload CSV
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>

              <div style={{ color: '#64748b', fontSize: 12, marginBottom: 16, lineHeight: 1.6 }}>
                Columns: <strong style={{ color: '#94a3b8' }}>email</strong> (required), <strong style={{ color: '#94a3b8' }}>password</strong> (required), <strong style={{ color: '#94a3b8' }}>name</strong>, <strong style={{ color: '#94a3b8' }}>role</strong> (client/sales/operations/admin), <strong style={{ color: '#94a3b8' }}>organization</strong> (6-digit org code or name), <strong style={{ color: '#94a3b8' }}>org_role</strong> (viewer/commenter/people_manager/client_admin)
              </div>

              {parseError && (
                <div style={{ background: 'rgba(127, 29, 29, 0.6)', color: '#fca5a5', padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 12 }}>
                  {parseError}
                </div>
              )}

              {unknownOrgs.length > 0 && (
                <div style={{ background: 'rgba(120, 80, 0, 0.4)', color: '#fcd34d', padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 12 }}>
                  Unknown organization{unknownOrgs.length > 1 ? 's' : ''}: <strong>{unknownOrgs.join(', ')}</strong> — these users will be created without an org assignment.
                </div>
              )}

              {/* Preview table */}
              {rows.length > 0 && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', marginBottom: 8 }}>
                    Preview ({rows.length} user{rows.length > 1 ? 's' : ''})
                  </div>
                  <div style={{
                    background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
                    maxHeight: 250, overflowY: 'auto' as const, overflowX: 'auto' as const,
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                          {['Email', 'Name', 'Role', 'Organization', 'Org Role'].map((h) => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '6px 12px', color: '#f1f5f9' }}>{r.email}</td>
                            <td style={{ padding: '6px 12px', color: '#94a3b8' }}>{r.name || '—'}</td>
                            <td style={{ padding: '6px 12px', color: '#94a3b8' }}>{r.role}</td>
                            <td style={{ padding: '6px 12px', color: r.organization && !knownOrgIds.has(r.organization.toLowerCase()) && !knownOrgIds.has(r.organization) ? '#fcd34d' : '#94a3b8' }}>
                              {r.organization || '—'}
                            </td>
                            <td style={{ padding: '6px 12px', color: '#94a3b8' }}>{r.orgRole}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                    <button onClick={onClose} style={modal.cancelBtn}>Cancel</button>
                    <button
                      onClick={handleImport}
                      disabled={importing || rows.length === 0}
                      style={modal.saveBtn}
                    >
                      {importing ? 'Importing...' : `Import ${rows.length} User${rows.length > 1 ? 's' : ''}`}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export function UsersPage() {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const cols = bp === 'desktop' ? COL_DESKTOP : COL_TABLET;

  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'client', clientId: '', clientRole: 'viewer' });
  const [createError, setCreateError] = useState('');

  // Filter
  const [filterClientId, setFilterClientId] = useState('');

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);

  // Modal
  const [editUser, setEditUser] = useState<any | null>(null);

  const load = () => {
    Promise.all([api.admin.getUsers(), api.admin.getClients()])
      .then(([u, c]) => { setUsers(u); setClients(c); setSelected(new Set()); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    try {
      await api.admin.createUser(form);
      setForm({ email: '', password: '', name: '', role: 'client', clientId: '', clientRole: 'viewer' });
      setShowCreate(false);
      load();
    } catch (err: any) {
      setCreateError(err.message);
    }
  };

  const adminCount = users.filter((u) => u.role === 'admin').length;
  const canDelete = (u: any) => u.id !== currentUser?.id && !(u.role === 'admin' && adminCount <= 1);
  const canChangeRole = (u: any) => !(u.role === 'admin' && adminCount <= 1);

  // Apply filter
  const filteredUsers = filterClientId
    ? users.filter((u) => u.clients?.some((c: any) => c.clientId === filterClientId))
    : users;

  // Only allow selecting deletable users
  const selectableUsers = filteredUsers.filter((u) => canDelete(u));

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectableUsers.every((u) => selected.has(u.id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableUsers.map((u) => u.id)));
    }
  };

  if (loading) return <div style={{ color: '#94a3b8' }}>Loading users...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{
        ...styles.header,
        ...(isMobile ? { flexDirection: 'column' as const, alignItems: 'stretch', gap: 12 } : {}),
      }}>
        <h1 style={{ ...styles.heading, ...(isMobile ? { fontSize: 20 } : {}) }}>Users</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={filterClientId}
            onChange={(e) => setFilterClientId(e.target.value)}
            style={{ ...styles.input, width: 'auto', minWidth: 160, fontSize: 13 }}
          >
            <option value="">All Organizations</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {selected.size > 0 && (
            <button onClick={() => setShowDeleteConfirm(true)} style={{
              ...styles.bulkDeleteBtn,
              ...(isMobile ? { minHeight: 44 } : {}),
            }}>
              Delete ({selected.size})
            </button>
          )}
          <button onClick={() => setShowCsvImport(true)} style={{
            ...styles.secondaryBtn,
            ...(isMobile ? { minHeight: 44 } : {}),
          }}>
            Import CSV
          </button>
          <button onClick={() => setShowCreate(!showCreate)} style={{
            ...styles.primaryBtn,
            ...(isMobile ? { minHeight: 44 } : {}),
          }}>
            {showCreate ? 'Cancel' : '+ New User'}
          </button>
        </div>
      </div>

      {/* ───── Create form ───── */}
      {showCreate && (
        <form onSubmit={handleCreate} style={styles.glassPanel}>
          {createError && <div style={styles.error}>{createError}</div>}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : '1fr 1fr 140px 100px',
            gap: 10,
          }}>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" style={styles.input} />
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" style={styles.input} required />
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" style={styles.input} required />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} style={styles.input}>
              <option value="client">Client</option>
              <option value="sales">Sales</option>
              <option value="operations">Operations</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {form.role === 'client' && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : form.clientId ? '1fr 140px' : '1fr',
              gap: 10, marginTop: 10,
            }}>
              <select value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })} style={styles.input}>
                <option value="">No organization</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {form.clientId && (
                <select value={form.clientRole} onChange={(e) => setForm({ ...form, clientRole: e.target.value })} style={styles.input}>
                  <option value="viewer">General Viewer</option>
                  <option value="commenter">User / Commenter</option>
                  <option value="people_manager">People Manager</option>
                  <option value="client_admin">Client Admin</option>
                </select>
              )}
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <button type="submit" style={{ ...styles.greenBtn, ...(isMobile ? { minHeight: 44, width: '100%' } : {}) }}>Create</button>
          </div>
        </form>
      )}

      {/* ───── User table ───── */}
      <div style={styles.glassTable}>

        {!isMobile && (
          <div style={{ ...styles.tableHeader, gridTemplateColumns: cols }}>
            <span>
              <input
                type="checkbox"
                checked={selectableUsers.length > 0 && selectableUsers.every((u) => selected.has(u.id))}
                onChange={toggleAll}
                style={styles.checkbox}
              />
            </span>
            <span>Name</span>
            <span>Email</span>
            {bp === 'desktop' && <span>Organization</span>}
            <span>Role</span>
            {bp === 'desktop' && <span style={{ textAlign: 'right' }}>Created</span>}
            <span style={{ textAlign: 'center' }}>Actions</span>
          </div>
        )}

        {filteredUsers.map((u) => (
          <div key={u.id}>
            {isMobile ? (
              <div style={styles.mobileCard}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                  {canDelete(u) && (
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggleSelect(u.id)}
                      style={styles.checkbox}
                    />
                  )}
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: u.profilePhotoPath ? `url(${u.profilePhotoPath}) center/cover` : 'rgba(79,124,255,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#4F7CFF', fontSize: 13, fontWeight: 700, border: '1px solid rgba(255,255,255,0.08)',
                  }}>
                    {!u.profilePhotoPath && (u.name || u.email || '?').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.mobileName}>{u.name || '—'}</div>
                    <div style={styles.mobileEmail}>{u.email}</div>
                  </div>
                </div>
                <div style={styles.mobileMetaRow}>
                  <span style={{ ...styles.roleBadge, color: u.role === 'admin' ? '#f59e0b' : u.role === 'sales' ? '#22c55e' : u.role === 'operations' ? '#a78bfa' : '#4F7CFF' }}>{u.role}</span>
                  {u.clients?.[0]?.clientName && <span style={{ color: '#94a3b8', fontSize: 12 }}>{u.clients[0].clientName}</span>}
                  <span style={styles.mobileDate}>{new Date(u.createdAt).toLocaleDateString()}</span>
                </div>
                <div style={styles.mobileActions}>
                  <button onClick={() => setEditUser(u)} style={{ ...styles.glassActionBtn, minHeight: 44, flex: 1 }}>
                    <PencilIcon size={13} /> Edit
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ ...styles.row, gridTemplateColumns: cols }}>
                <span>
                  {canDelete(u) ? (
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggleSelect(u.id)}
                      style={styles.checkbox}
                    />
                  ) : <span style={{ width: 16, display: 'inline-block' }} />}
                </span>
                <span style={{ ...styles.cellTruncate, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    background: u.profilePhotoPath ? `url(${u.profilePhotoPath}) center/cover` : 'rgba(79,124,255,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#4F7CFF', fontSize: 11, fontWeight: 700, border: '1px solid rgba(255,255,255,0.06)',
                  }}>
                    {!u.profilePhotoPath && (u.name || u.email || '?').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <span style={{ fontWeight: 500, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{u.name || '—'}</span>
                </span>
                <span style={{ ...styles.cellTruncate, color: '#94a3b8', fontSize: 13 }}>{u.email}</span>
                {bp === 'desktop' && (
                  <span style={{ ...styles.cellTruncate, color: '#94a3b8', fontSize: 13 }}>
                    {u.clients?.[0]?.clientName || <span style={{ color: '#475569' }}>—</span>}
                  </span>
                )}
                <span style={{ ...styles.roleBadge, color: u.role === 'admin' ? '#f59e0b' : u.role === 'sales' ? '#22c55e' : u.role === 'operations' ? '#a78bfa' : '#4F7CFF' }}>{u.role}</span>
                {bp === 'desktop' && (
                  <span style={styles.cellDate}>{new Date(u.createdAt).toLocaleDateString()}</span>
                )}
                <span style={styles.cellActions}>
                  <button onClick={() => setEditUser(u)} style={styles.glassActionBtn} title="Edit User">
                    <PencilIcon size={12} />
                    {bp === 'desktop' && <span>Edit</span>}
                  </button>
                  {/* Delete — visibility:hidden if not allowed, keeps spacing */}
                  <button
                    onClick={() => canDelete(u) && confirm('Delete this user?') && api.admin.deleteUser(u.id).then(load)}
                    style={{ ...styles.glassDangerBtn, ...(canDelete(u) ? {} : { visibility: 'hidden' as const }) }}
                    title="Delete User"
                  >
                    {bp === 'desktop' ? 'Delete' : '\u00D7'}
                  </button>
                </span>
              </div>
            )}
          </div>
        ))}
        {filteredUsers.length === 0 && (
          <div style={{ padding: 20, color: '#64748b', textAlign: 'center', fontSize: 14 }}>
            {filterClientId ? 'No users in this organization' : 'No users'}
          </div>
        )}
      </div>

      {/* ───── Bulk Delete Confirm ───── */}
      {showDeleteConfirm && (
        <BulkDeleteUsersModal
          ids={Array.from(selected)}
          onClose={() => setShowDeleteConfirm(false)}
          onDeleted={() => { setShowDeleteConfirm(false); load(); }}
        />
      )}

      {/* ───── CSV Import Modal ───── */}
      {showCsvImport && (
        <CsvImportModal
          clients={clients}
          onClose={() => setShowCsvImport(false)}
          onImported={() => { setShowCsvImport(false); load(); }}
        />
      )}

      {/* ───── Edit Modal ───── */}
      {editUser && (
        <EditUserModal
          user={editUser}
          clients={clients}
          canChangeRole={canChangeRole(editUser)}
          canDelete={canDelete(editUser)}
          onClose={() => setEditUser(null)}
          onSaved={load}
          onDeleted={load}
        />
      )}
    </div>
  );
}

// ─── Table styles ───────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20,
  },
  heading: { fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0 },

  primaryBtn: {
    background: 'rgba(79, 124, 255, 0.85)', color: '#fff', border: '1px solid rgba(79, 124, 255, 0.3)',
    borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap' as const,
    backdropFilter: 'blur(12px)',
    boxShadow: '0 2px 8px rgba(79, 124, 255, 0.2)',
  },
  bulkDeleteBtn: {
    background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap' as const,
  },
  secondaryBtn: {
    background: 'rgba(255, 255, 255, 0.04)', color: '#94a3b8', border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 500,
    cursor: 'pointer', whiteSpace: 'nowrap' as const,
    backdropFilter: 'blur(12px)',
  },
  checkbox: {
    width: 16, height: 16, cursor: 'pointer', accentColor: '#4F7CFF',
  },
  greenBtn: {
    background: 'rgba(34, 197, 94, 0.85)', color: '#fff', border: '1px solid rgba(34, 197, 94, 0.3)',
    borderRadius: 8, padding: '6px 16px', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap' as const,
    backdropFilter: 'blur(12px)',
  },
  glassActionBtn: {
    background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.1)',
    color: '#94a3b8', borderRadius: 8, padding: '4px 10px', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 5,
    flexShrink: 0, fontSize: 11, whiteSpace: 'nowrap' as const,
    backdropFilter: 'blur(8px)',
    transition: 'background 0.15s, border-color 0.15s',
  },
  glassDangerBtn: {
    background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)',
    color: '#ef4444', borderRadius: 8, padding: '4px 10px', fontSize: 11,
    cursor: 'pointer', whiteSpace: 'nowrap' as const, flexShrink: 0,
    backdropFilter: 'blur(8px)',
    transition: 'background 0.15s, border-color 0.15s',
  },

  glassPanel: {
    background: GLASS_BG, backdropFilter: GLASS_BLUR, WebkitBackdropFilter: GLASS_BLUR,
    border: GLASS_BORDER, borderRadius: 16, padding: 20, marginBottom: 16,
    boxShadow: GLASS_SHADOW,
  },
  input: {
    background: 'rgba(0, 0, 0, 0.25)', border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: 8, padding: '8px 12px', color: '#f1f5f9', fontSize: 13,
    outline: 'none', boxSizing: 'border-box' as const, width: '100%', minWidth: 0,
    backdropFilter: 'blur(8px)',
  },
  error: {
    background: 'rgba(127, 29, 29, 0.6)', color: '#fca5a5', padding: '8px 14px',
    borderRadius: 10, fontSize: 13, marginBottom: 12,
    border: '1px solid rgba(239, 68, 68, 0.2)', backdropFilter: 'blur(8px)',
  },

  glassTable: {
    background: GLASS_BG, backdropFilter: GLASS_BLUR, WebkitBackdropFilter: GLASS_BLUR,
    border: GLASS_BORDER, borderRadius: 16, overflow: 'hidden',
    boxShadow: GLASS_SHADOW,
  },
  tableHeader: {
    display: 'grid', padding: '10px 20px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
    color: '#64748b', fontSize: 12, fontWeight: 600,
    textTransform: 'uppercase' as const, letterSpacing: '0.05em',
    gap: 16, alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.02)',
  },
  row: {
    display: 'grid', alignItems: 'center', padding: '10px 20px',
    borderBottom: ROW_DIVIDER, fontSize: 14, gap: 16,
    transition: 'background 0.15s',
  },
  cellTruncate: {
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
    minWidth: 0,
  },
  roleBadge: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const },
  cellDate: { color: '#64748b', fontSize: 13, textAlign: 'right' as const },
  cellActions: { display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' },

  mobileCard: { padding: 16, borderBottom: ROW_DIVIDER },
  mobileName: { fontWeight: 500, color: '#f1f5f9', fontSize: 14, marginBottom: 0 },
  mobileEmail: { color: '#94a3b8', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  mobileMetaRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  mobileDate: { color: '#64748b', fontSize: 13 },
  mobileActions: { display: 'flex', gap: 8 },
};
